import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router-dom';

import { matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ReportSchedulingView } from '@futrli/views/ReportSchedulingView';
import { MembersView } from '@futrli/views/MembersView';
import { useApiView } from '@futrli/views/hooks/useApiView';

import { ExportedPdfRowItem } from '../types';

/**
 * Fetches exported and dispatched reports and combines them into a flat list.
 * Also fetches workspace members for creator name resolution.
 *
 * @returns allItems - Combined list of exported and dispatched reports with a discriminated `type` field.
 * @returns members - Workspace members for the current organisation.
 * @returns isLoading - Whether either data source is still loading.
 * @returns errorLoading - Whether either data source encountered an error.
 */
export const useExportedReports = () => {
  const flags = useFlags();
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const view = useApiView(
    useMemo(
      () => new ReportSchedulingView(workspaceId, organisationId, isConsolidation, {
        schedules: false,
        awaitingReview: false,
        dispatched: true,
        exported: true
      }, flags),
      [workspaceId, organisationId, isConsolidation, flags]
    )
  );

  const membersView = useApiView(
    useMemo(
      () => new MembersView(workspaceId, organisationId, isConsolidation),
      [workspaceId, organisationId, isConsolidation]
    )
  );

  const allItems: ExportedPdfRowItem[] = view.state.exportedPdfItems ?? [];

  return {
    allItems,
    members: membersView.state.members,
    isLoading: view.state.isLoading || membersView.state.isLoading,
    errorLoading: view.state.errorLoading || membersView.state.errorLoading
  };
};
